import { useState, useEffect, useCallback } from "react";
import {
  Scissors,
  Loader2,
  Play,
  Download,
  Share2,
  Copy,
  Check,
  Clock,
  Film,
  Pencil,
  Archive,
  ArchiveRestore,
  X,
  Eye,
  EyeOff,
  CheckSquare,
  Square,
  MinusSquare,
} from "lucide-react";
import { colors, fonts } from "../../styles/theme";
import { SearchBar } from "../../components/ui";
import { fetchClips, updateClip, bulkUpdateClips } from "../../services/api";

const CATEGORY_FILTERS = [
  { id: "", label: "All" },
  { id: "insight", label: "Insight", color: "#6366f1" },
  { id: "qa", label: "Q&A", color: "#f59e0b" },
  { id: "debate", label: "Debate", color: "#ef4444" },
  { id: "story", label: "Story", color: "#8b5cf6" },
  { id: "quote", label: "Quote", color: "#06b6d4" },
  { id: "energy", label: "Energy", color: "#f97316" },
  { id: "humour", label: "Humour", color: "#ec4899" },
];

const SOURCE_FILTERS = [
  { id: "", label: "All Sources" },
  { id: "zoom", label: "Zoom" },
  { id: "zoom-webinar", label: "Webinar" },
  { id: "zoom-clip", label: "Zoom Clip" },
  { id: "upload", label: "Upload" },
  { id: "tv", label: "TV" },
];

function fmtDuration(seconds) {
  if (!seconds) return "—";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function fmtTimestamp(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function fmtDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const categoryLabels = {
  insight: { label: "Insight", color: "#6366f1" },
  qa: { label: "Q&A", color: "#f59e0b" },
  debate: { label: "Debate", color: "#ef4444" },
  story: { label: "Story", color: "#8b5cf6" },
  quote: { label: "Quote", color: "#06b6d4" },
  energy: { label: "Energy", color: "#f97316" },
  humour: { label: "Humour", color: "#ec4899" },
};

// ── Clip Card ──────────────────────────────────────────────

function ClipCard({ clip, onUpdate, selectionMode, selected, onToggleSelect }) {
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editTitle, setEditTitle] = useState(clip.title || "");
  const [editSpeaker, setEditSpeaker] = useState(clip.speaker || "");
  const [editCategory, setEditCategory] = useState(clip.aiCategory || "");

  const clipKey = `${clip.videoId}::${clip.id}`;

  const badge = clip.aiCategory
    ? categoryLabels[clip.aiCategory] || { label: clip.aiCategory, color: colors.textSecondary }
    : { label: "Clip", color: colors.textSecondary };

  const handleCopy = () => {
    if (!clip.url) return;
    navigator.clipboard.writeText(clip.url);
    setCopiedUrl(true);
    setTimeout(() => setCopiedUrl(false), 2000);
  };

  const handleDownload = () => {
    if (!clip.url) return;
    const a = document.createElement("a");
    a.href = clip.url;
    a.download = `${clip.title || "clip"}.mp4`;
    a.click();
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateClip(clip.videoId, clip.id, {
        containerType: clip.containerType,
        title: editTitle,
        speaker: editSpeaker,
        aiCategory: editCategory,
      });
      setEditing(false);
      onUpdate?.();
    } catch {
      // stay in edit mode on error
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async () => {
    setSaving(true);
    try {
      await updateClip(clip.videoId, clip.id, {
        containerType: clip.containerType,
        archived: !clip.archived,
      });
      onUpdate?.();
    } finally {
      setSaving(false);
    }
  };

  const startEdit = () => {
    setEditTitle(clip.title || "");
    setEditSpeaker(clip.speaker || "");
    setEditCategory(clip.aiCategory || "");
    setEditing(true);
  };

  const inputStyle = {
    width: "100%",
    padding: "5px 8px",
    borderRadius: 4,
    border: `1px solid ${colors.border}`,
    background: colors.bgCard,
    color: colors.text,
    fontSize: 12,
    fontFamily: fonts.body,
    outline: "none",
  };

  const selectStyle = {
    ...inputStyle,
    cursor: "pointer",
  };

  return (
    <div
      onClick={selectionMode ? () => onToggleSelect?.(clipKey) : undefined}
      style={{
        background: colors.bgCard,
        border: `1px solid ${selected ? colors.accent : clip.archived ? "rgba(239,68,68,0.3)" : colors.border}`,
        borderRadius: 10,
        overflow: "hidden",
        transition: "all 0.15s",
        opacity: clip.archived ? 0.6 : 1,
        cursor: selectionMode ? "pointer" : "default",
        boxShadow: selected ? `0 0 0 2px ${colors.accent}40` : "none",
        position: "relative",
      }}
    >
      {/* Selection checkbox */}
      {selectionMode && (
        <div
          style={{
            position: "absolute",
            top: 8,
            left: 8,
            zIndex: 2,
            background: selected ? colors.accent : "rgba(0,0,0,0.5)",
            borderRadius: 4,
            width: 22,
            height: 22,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
        >
          {selected ? <Check size={14} color="#fff" /> : <Square size={14} color="#fff" />}
        </div>
      )}

      {/* Video player */}
      {clip.url ? (
        <video
          controls
          preload="metadata"
          style={{
            width: "100%",
            display: "block",
            background: "#000",
            maxHeight: 200,
          }}
        >
          <source src={clip.url} type={clip.mimeType || "video/mp4"} />
        </video>
      ) : (
        <div
          style={{
            width: "100%",
            aspectRatio: "16/9",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.06)",
          }}
        >
          <Play size={28} color={colors.textSecondary} style={{ opacity: 0.3 }} />
        </div>
      )}

      {/* Info */}
      <div style={{ padding: "12px 14px" }}>
        {editing ? (
          /* ── Edit Mode ── */
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <div>
              <label style={{ fontSize: 10, color: colors.textSecondary, display: "block", marginBottom: 2 }}>Title</label>
              <input
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                style={inputStyle}
                autoFocus
              />
            </div>
            <div>
              <label style={{ fontSize: 10, color: colors.textSecondary, display: "block", marginBottom: 2 }}>Speaker</label>
              <input
                value={editSpeaker}
                onChange={(e) => setEditSpeaker(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={{ fontSize: 10, color: colors.textSecondary, display: "block", marginBottom: 2 }}>Category</label>
              <select
                value={editCategory}
                onChange={(e) => setEditCategory(e.target.value)}
                style={selectStyle}
              >
                <option value="">None</option>
                {Object.entries(categoryLabels).map(([key, { label }]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  flex: 1,
                  padding: "6px 0",
                  borderRadius: 4,
                  border: "none",
                  background: colors.accent,
                  color: "#fff",
                  fontSize: 12,
                  fontFamily: fonts.body,
                  fontWeight: 600,
                  cursor: saving ? "wait" : "pointer",
                  opacity: saving ? 0.7 : 1,
                }}
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button
                onClick={() => setEditing(false)}
                style={{
                  padding: "6px 12px",
                  borderRadius: 4,
                  border: `1px solid ${colors.border}`,
                  background: "none",
                  color: colors.textSecondary,
                  fontSize: 12,
                  fontFamily: fonts.body,
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          /* ── Display Mode ── */
          <>
            <div style={{ fontSize: 13, fontWeight: 600, color: colors.text, marginBottom: 4 }}>
              {clip.title || "Untitled clip"}
              {clip.archived && (
                <span style={{ fontSize: 10, color: colors.danger, fontWeight: 400, marginLeft: 6 }}>archived</span>
              )}
            </div>

        {/* Badges row */}
        <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 6 }}>
          <span
            style={{
              fontSize: 10,
              padding: "2px 6px",
              borderRadius: 4,
              background: `${badge.color}18`,
              color: badge.color,
              fontWeight: 600,
            }}
          >
            {badge.label}
          </span>
          {clip.speaker && clip.speaker !== "unknown" && (
            <span
              style={{
                fontSize: 10,
                padding: "2px 6px",
                borderRadius: 4,
                background: "rgba(99,102,241,0.08)",
                color: colors.info,
                fontWeight: 600,
              }}
            >
              {clip.speaker}
            </span>
          )}
          {clip.durationSeconds > 0 && (
            <span style={{ fontSize: 11, color: colors.textSecondary, display: "flex", alignItems: "center", gap: 3 }}>
              <Clock size={10} /> {fmtDuration(clip.durationSeconds)}
            </span>
          )}
        </div>

        {/* Source video */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            fontSize: 11,
            color: colors.textSecondary,
            marginBottom: 6,
          }}
        >
          <Film size={10} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {clip.videoTitle}
          </span>
          {clip.videoDate && <span>· {fmtDate(clip.videoDate)}</span>}
        </div>

        {/* Transcript excerpt */}
        {clip.transcript && (
          <div
            style={{
              fontSize: 12,
              color: colors.textSecondary,
              lineHeight: 1.5,
              padding: 8,
              background: "rgba(0,0,0,0.02)",
              borderRadius: 4,
              maxHeight: 48,
              overflow: "hidden",
              marginBottom: 6,
            }}
          >
            &ldquo;{clip.transcript.substring(0, 150)}
            {clip.transcript.length > 150 ? "..." : ""}&rdquo;
          </div>
        )}

        {/* AI reason */}
        {clip.aiReason && (
          <div
            style={{
              fontSize: 11,
              color: colors.textSecondary,
              fontStyle: "italic",
              marginBottom: 6,
            }}
          >
            AI: {clip.aiReason}
          </div>
        )}

        {/* Actions */}
        <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
          {clip.url && (
            <>
              <button
                onClick={handleDownload}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "4px 8px",
                  background: "none",
                  border: `1px solid ${colors.border}`,
                  borderRadius: 4,
                  color: colors.textSecondary,
                  fontSize: 11,
                  fontFamily: fonts.body,
                  cursor: "pointer",
                }}
              >
                <Download size={11} /> Download
              </button>
              <button
                onClick={handleCopy}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "4px 8px",
                  background: "none",
                  border: `1px solid ${colors.border}`,
                  borderRadius: 4,
                  color: colors.textSecondary,
                  fontSize: 11,
                  fontFamily: fonts.body,
                  cursor: "pointer",
                }}
              >
                {copiedUrl ? <Check size={11} /> : <Share2 size={11} />}
                {copiedUrl ? "Copied!" : "Copy Link"}
              </button>
            </>
          )}
          <button
            onClick={startEdit}
            title="Edit clip"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 8px",
              background: "none",
              border: `1px solid ${colors.border}`,
              borderRadius: 4,
              color: colors.textSecondary,
              fontSize: 11,
              fontFamily: fonts.body,
              cursor: "pointer",
              marginLeft: "auto",
            }}
          >
            <Pencil size={11} />
          </button>
          <button
            onClick={handleArchive}
            disabled={saving}
            title={clip.archived ? "Restore clip" : "Archive clip"}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 4,
              padding: "4px 8px",
              background: "none",
              border: `1px solid ${clip.archived ? "rgba(34,197,94,0.3)" : "rgba(239,68,68,0.3)"}`,
              borderRadius: 4,
              color: clip.archived ? colors.success : colors.danger,
              fontSize: 11,
              fontFamily: fonts.body,
              cursor: saving ? "wait" : "pointer",
              opacity: saving ? 0.5 : 1,
            }}
          >
            {clip.archived ? <ArchiveRestore size={11} /> : <Archive size={11} />}
          </button>
        </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────

export default function ClipLibrary() {
  const [clips, setClips] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedClips, setSelectedClips] = useState(new Set());
  const [bulkAction, setBulkAction] = useState(null); // 'archive' | 'restore' | 'category' | 'speaker'
  const [bulkCategory, setBulkCategory] = useState("");
  const [bulkSpeaker, setBulkSpeaker] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  const toggleSelect = useCallback((clipKey) => {
    setSelectedClips((prev) => {
      const next = new Set(prev);
      if (next.has(clipKey)) next.delete(clipKey);
      else next.add(clipKey);
      return next;
    });
  }, []);

  const selectAll = useCallback(() => {
    const allKeys = clips.map((c) => `${c.videoId}::${c.id}`);
    setSelectedClips((prev) => {
      if (prev.size === allKeys.length) return new Set(); // deselect all
      return new Set(allKeys);
    });
  }, [clips]);

  const exitSelectionMode = useCallback(() => {
    setSelectionMode(false);
    setSelectedClips(new Set());
    setBulkAction(null);
  }, []);

  const getSelectedClipObjects = useCallback(() => {
    return clips.filter((c) => selectedClips.has(`${c.videoId}::${c.id}`));
  }, [clips, selectedClips]);

  const loadClips = useCallback(() => {
    setLoading(true);
    setError(null);
    fetchClips({ search, category: categoryFilter, source: sourceFilter, showArchived })
      .then((data) => {
        setClips(data.clips || []);
        setTotal(data.total || 0);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, categoryFilter, sourceFilter, showArchived]);

  const handleBulkAction = useCallback(async () => {
    const selected = getSelectedClipObjects();
    if (selected.length === 0) return;

    let operations;
    if (bulkAction === "archive") {
      operations = selected.map((c) => ({
        videoId: c.videoId, clipId: c.id, containerType: c.containerType,
        updates: { archived: true },
      }));
    } else if (bulkAction === "restore") {
      operations = selected.map((c) => ({
        videoId: c.videoId, clipId: c.id, containerType: c.containerType,
        updates: { archived: false },
      }));
    } else if (bulkAction === "category" && bulkCategory) {
      operations = selected.map((c) => ({
        videoId: c.videoId, clipId: c.id, containerType: c.containerType,
        updates: { aiCategory: bulkCategory },
      }));
    } else if (bulkAction === "speaker" && bulkSpeaker.trim()) {
      operations = selected.map((c) => ({
        videoId: c.videoId, clipId: c.id, containerType: c.containerType,
        updates: { speaker: bulkSpeaker.trim() },
      }));
    } else {
      return;
    }

    setBulkSaving(true);
    try {
      await bulkUpdateClips(operations);
      exitSelectionMode();
      loadClips();
    } catch {
      // stay in selection mode on error
    } finally {
      setBulkSaving(false);
    }
  }, [bulkAction, bulkCategory, bulkSpeaker, getSelectedClipObjects, exitSelectionMode, loadClips]);

  useEffect(() => {
    const timer = setTimeout(loadClips, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [loadClips]);

  return (
    <div>
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: 24,
        }}
      >
        <div>
          <h1
            style={{
              fontFamily: fonts.heading,
              fontSize: 28,
              fontWeight: 700,
              color: colors.text,
              marginBottom: 4,
            }}
          >
            Clip Library
          </h1>
          <p
            style={{
              fontFamily: fonts.body,
              fontSize: 14,
              color: colors.textSecondary,
            }}
          >
            {loading
              ? "Loading..."
              : `${total} clip${total !== 1 ? "s" : ""} from all videos`}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <SearchBar value={search} onChange={setSearch} placeholder="Search clips..." />
          <button
            onClick={() => selectionMode ? exitSelectionMode() : setSelectionMode(true)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              padding: "8px 14px",
              borderRadius: 6,
              border: `1px solid ${selectionMode ? colors.accent : colors.border}`,
              background: selectionMode ? colors.accentDim : "transparent",
              color: selectionMode ? colors.accent : colors.textSecondary,
              fontSize: 13,
              fontFamily: fonts.body,
              fontWeight: selectionMode ? 600 : 400,
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "all 0.15s",
            }}
          >
            {selectionMode ? <X size={14} /> : <CheckSquare size={14} />}
            {selectionMode ? "Cancel" : "Select"}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 20, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        {/* Category filters */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {CATEGORY_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setCategoryFilter(f.id)}
              style={{
                padding: "5px 10px",
                borderRadius: 6,
                border: `1px solid ${categoryFilter === f.id ? (f.color || colors.accent) : colors.border}`,
                background: categoryFilter === f.id ? `${f.color || colors.accent}14` : "transparent",
                color: categoryFilter === f.id ? (f.color || colors.accent) : colors.textSecondary,
                fontSize: 12,
                fontFamily: fonts.body,
                fontWeight: categoryFilter === f.id ? 600 : 400,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Source filters */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {SOURCE_FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setSourceFilter(f.id)}
              style={{
                padding: "5px 10px",
                borderRadius: 6,
                border: `1px solid ${sourceFilter === f.id ? colors.accent : colors.border}`,
                background: sourceFilter === f.id ? colors.accentDim : "transparent",
                color: sourceFilter === f.id ? colors.accent : colors.textSecondary,
                fontSize: 12,
                fontFamily: fonts.body,
                fontWeight: sourceFilter === f.id ? 600 : 400,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Show archived toggle */}
        <button
          onClick={() => setShowArchived(!showArchived)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            padding: "5px 10px",
            borderRadius: 6,
            border: `1px solid ${showArchived ? "rgba(239,68,68,0.4)" : colors.border}`,
            background: showArchived ? "rgba(239,68,68,0.08)" : "transparent",
            color: showArchived ? colors.danger : colors.textSecondary,
            fontSize: 12,
            fontFamily: fonts.body,
            fontWeight: showArchived ? 600 : 400,
            cursor: "pointer",
            transition: "all 0.15s",
            marginLeft: "auto",
          }}
        >
          {showArchived ? <Eye size={12} /> : <EyeOff size={12} />}
          {showArchived ? "Showing archived" : "Show archived"}
        </button>
      </div>

      {/* Bulk actions toolbar */}
      {selectionMode && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "10px 14px",
            marginBottom: 16,
            borderRadius: 8,
            background: colors.bgCard,
            border: `1px solid ${colors.border}`,
            flexWrap: "wrap",
          }}
        >
          {/* Select all */}
          <button
            onClick={selectAll}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
              padding: "5px 10px",
              borderRadius: 4,
              border: `1px solid ${colors.border}`,
              background: "none",
              color: colors.textSecondary,
              fontSize: 12,
              fontFamily: fonts.body,
              cursor: "pointer",
            }}
          >
            {selectedClips.size === clips.length && clips.length > 0 ? <CheckSquare size={13} /> : selectedClips.size > 0 ? <MinusSquare size={13} /> : <Square size={13} />}
            {selectedClips.size === clips.length && clips.length > 0 ? "Deselect all" : "Select all"}
          </button>

          <span style={{ fontSize: 12, color: colors.textSecondary, fontFamily: fonts.body }}>
            {selectedClips.size} selected
          </span>

          <div style={{ width: 1, height: 20, background: colors.border }} />

          {/* Quick actions */}
          <button
            onClick={() => { setBulkAction("archive"); setBulkCategory(""); setBulkSpeaker(""); }}
            disabled={selectedClips.size === 0}
            style={{
              padding: "5px 10px",
              borderRadius: 4,
              border: `1px solid ${bulkAction === "archive" ? "rgba(239,68,68,0.4)" : colors.border}`,
              background: bulkAction === "archive" ? "rgba(239,68,68,0.08)" : "none",
              color: bulkAction === "archive" ? colors.danger : colors.textSecondary,
              fontSize: 12,
              fontFamily: fonts.body,
              fontWeight: bulkAction === "archive" ? 600 : 400,
              cursor: selectedClips.size === 0 ? "not-allowed" : "pointer",
              opacity: selectedClips.size === 0 ? 0.4 : 1,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <Archive size={12} /> Archive
          </button>

          <button
            onClick={() => { setBulkAction("restore"); setBulkCategory(""); setBulkSpeaker(""); }}
            disabled={selectedClips.size === 0}
            style={{
              padding: "5px 10px",
              borderRadius: 4,
              border: `1px solid ${bulkAction === "restore" ? "rgba(34,197,94,0.4)" : colors.border}`,
              background: bulkAction === "restore" ? "rgba(34,197,94,0.08)" : "none",
              color: bulkAction === "restore" ? colors.success : colors.textSecondary,
              fontSize: 12,
              fontFamily: fonts.body,
              fontWeight: bulkAction === "restore" ? 600 : 400,
              cursor: selectedClips.size === 0 ? "not-allowed" : "pointer",
              opacity: selectedClips.size === 0 ? 0.4 : 1,
              display: "flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            <ArchiveRestore size={12} /> Restore
          </button>

          <button
            onClick={() => { setBulkAction(bulkAction === "category" ? null : "category"); setBulkSpeaker(""); }}
            disabled={selectedClips.size === 0}
            style={{
              padding: "5px 10px",
              borderRadius: 4,
              border: `1px solid ${bulkAction === "category" ? colors.accent : colors.border}`,
              background: bulkAction === "category" ? colors.accentDim : "none",
              color: bulkAction === "category" ? colors.accent : colors.textSecondary,
              fontSize: 12,
              fontFamily: fonts.body,
              fontWeight: bulkAction === "category" ? 600 : 400,
              cursor: selectedClips.size === 0 ? "not-allowed" : "pointer",
              opacity: selectedClips.size === 0 ? 0.4 : 1,
            }}
          >
            Set Category
          </button>

          <button
            onClick={() => { setBulkAction(bulkAction === "speaker" ? null : "speaker"); setBulkCategory(""); }}
            disabled={selectedClips.size === 0}
            style={{
              padding: "5px 10px",
              borderRadius: 4,
              border: `1px solid ${bulkAction === "speaker" ? colors.accent : colors.border}`,
              background: bulkAction === "speaker" ? colors.accentDim : "none",
              color: bulkAction === "speaker" ? colors.accent : colors.textSecondary,
              fontSize: 12,
              fontFamily: fonts.body,
              fontWeight: bulkAction === "speaker" ? 600 : 400,
              cursor: selectedClips.size === 0 ? "not-allowed" : "pointer",
              opacity: selectedClips.size === 0 ? 0.4 : 1,
            }}
          >
            Set Speaker
          </button>

          {/* Inline editor for category/speaker */}
          {bulkAction === "category" && (
            <select
              value={bulkCategory}
              onChange={(e) => setBulkCategory(e.target.value)}
              style={{
                padding: "5px 8px",
                borderRadius: 4,
                border: `1px solid ${colors.border}`,
                background: colors.bgCard,
                color: colors.text,
                fontSize: 12,
                fontFamily: fonts.body,
              }}
            >
              <option value="">Choose category...</option>
              {Object.entries(categoryLabels).map(([key, { label }]) => (
                <option key={key} value={key}>{label}</option>
              ))}
            </select>
          )}
          {bulkAction === "speaker" && (
            <input
              value={bulkSpeaker}
              onChange={(e) => setBulkSpeaker(e.target.value)}
              placeholder="Speaker name..."
              style={{
                padding: "5px 8px",
                borderRadius: 4,
                border: `1px solid ${colors.border}`,
                background: colors.bgCard,
                color: colors.text,
                fontSize: 12,
                fontFamily: fonts.body,
                width: 160,
              }}
            />
          )}

          {/* Apply button */}
          {bulkAction && (
            <button
              onClick={handleBulkAction}
              disabled={bulkSaving || selectedClips.size === 0}
              style={{
                padding: "5px 14px",
                borderRadius: 4,
                border: "none",
                background: colors.accent,
                color: "#fff",
                fontSize: 12,
                fontFamily: fonts.body,
                fontWeight: 600,
                cursor: bulkSaving ? "wait" : "pointer",
                opacity: bulkSaving ? 0.7 : 1,
                marginLeft: 4,
              }}
            >
              {bulkSaving ? "Applying..." : `Apply to ${selectedClips.size}`}
            </button>
          )}
        </div>
      )}

      {/* Error */}
      {error && (
        <div
          style={{
            padding: "10px 16px",
            borderRadius: 8,
            background: "rgba(239,68,68,0.08)",
            color: colors.danger,
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          Failed to load clips: {error}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: 60,
            color: colors.textSecondary,
          }}
        >
          <Loader2 size={24} className="spin" />
        </div>
      )}

      {/* Empty state */}
      {!loading && clips.length === 0 && !error && (
        <div
          style={{
            textAlign: "center",
            padding: 60,
            color: colors.textSecondary,
          }}
        >
          <Scissors size={40} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>No clips yet</p>
          <p style={{ fontSize: 13, margin: "4px 0 0" }}>
            Clips will appear here once videos have been transcribed and processed
          </p>
        </div>
      )}

      {/* Clips grid */}
      {!loading && clips.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 16,
          }}
        >
          {clips.map((clip) => (
            <ClipCard
              key={clip.id || `${clip.videoId}-${clip.startSeconds}`}
              clip={clip}
              onUpdate={loadClips}
              selectionMode={selectionMode}
              selected={selectedClips.has(`${clip.videoId}::${clip.id}`)}
              onToggleSelect={toggleSelect}
            />
          ))}
        </div>
      )}

    </div>
  );
}
